import Anthropic from "@anthropic-ai/sdk";
import { ConvexReactClient } from "convex/react";
import { AppStore } from "@/store";
import { api } from "@/smart-views/client/api";
import { logger } from "@/modules/logger";
import { listRecentNotes } from "./note-access";

const MODEL = "claude-opus-4-7";
const NOTE_BUDGET = 30;
const STALE_AFTER_MS = 60 * 60 * 1000; // recompute at most hourly

let inFlight = false;

const SYSTEM_PROMPT = `You're proposing "Smart Views" — structured live dashboards — for a knowledge worker based on the topics that show up in their recent notes.

A good Smart View suggestion is:
- Specific enough to track meaningfully (e.g. "Acme Co deal", "Q2 product plan", not "Work").
- Backed by multiple notes — there's enough material to compose a useful dashboard.
- Distinct from the user's existing Smart Views (you'll be given those).
- Written as the title the user would actually use (proper nouns, plain casing).

Return 5–8 suggestions via the propose_suggestions tool. Skip items that overlap with existing views. If the user only has one or two notes about a topic, skip it.`;

const TOOL: Anthropic.Tool = {
  name: "propose_suggestions",
  description: "Submit the proposed Smart View titles with one-sentence rationale each.",
  input_schema: {
    type: "object",
    properties: {
      suggestions: {
        type: "array",
        items: {
          type: "object",
          properties: {
            title: { type: "string" },
            rationale: {
              type: "string",
              description: "One concise sentence: why this would be a useful Smart View.",
            },
          },
          required: ["title", "rationale"],
        },
      },
    },
    required: ["suggestions"],
  },
};

export const runSuggestionAgent = async ({
  store,
  convex,
  force = false,
}: {
  store: AppStore;
  convex: ConvexReactClient;
  force?: boolean;
}): Promise<void> => {
  if (inFlight) return;

  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY as string | undefined;
  if (!apiKey) return;

  // Skip if we have fresh suggestions already and nobody asked us to force.
  if (!force) {
    try {
      const latest = await convex.query(api.suggestions.latest, {});
      if (latest && Date.now() - latest.computedAt < STALE_AFTER_MS) return;
    } catch {
      /* fall through and recompute */
    }
  }

  inFlight = true;
  try {
    const [notes, existing] = await Promise.all([
      listRecentNotes(store, NOTE_BUDGET),
      convex.query(api.smartViews.client.listSmartViews, {}),
    ]);

    if (notes.length === 0) {
      await convex.mutation(api.suggestions.setSuggestions, { suggestions: [] });
      return;
    }

    const existingTitles = existing.map(v => v.title);
    const existingTitlesLower = new Set(existingTitles.map(t => t.toLowerCase()));

    const userPayload = [
      `Existing Smart Views (avoid duplicates):\n${
        existingTitles.length > 0 ? existingTitles.map(t => `- ${t}`).join("\n") : "(none)"
      }`,
      `\nRecent notes (most recent first):\n${notes
        .map(n => `### ${n.title}\nupdated: ${n.updated_at}\n${n.body.slice(0, 800)}`)
        .join("\n\n")}`,
    ].join("\n");

    const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });

    const response = await client.beta.messages.create({
      model: MODEL,
      max_tokens: 2048,
      system: [
        {
          type: "text",
          text: SYSTEM_PROMPT,
          cache_control: { type: "ephemeral" },
        },
      ],
      tools: [TOOL],
      tool_choice: { type: "tool", name: "propose_suggestions" },
      messages: [{ role: "user", content: userPayload }],
    });

    const toolUse = response.content.find(
      (b): b is Anthropic.Beta.BetaToolUseBlock => b.type === "tool_use"
    );
    if (!toolUse) {
      logger.warn({ message: "Suggestion agent: model did not call the tool" });
      return;
    }

    const raw =
      (toolUse.input as { suggestions?: Array<{ title?: string; rationale?: string }> })
        .suggestions ?? [];

    const cleaned = raw
      .map(s => ({
        title: typeof s.title === "string" ? s.title.trim() : "",
        rationale: typeof s.rationale === "string" ? s.rationale.trim() : "",
      }))
      .filter(s => s.title.length > 0)
      .filter(s => !existingTitlesLower.has(s.title.toLowerCase()));

    await convex.mutation(api.suggestions.setSuggestions, { suggestions: cleaned });
  } catch (err) {
    logger.warn({
      message: "Suggestion agent run failed",
      info: { err: String(err) },
    });
  } finally {
    inFlight = false;
  }
};
